var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { EarlyVotingPolicy } from "@/gql/graphql";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { GET_AREAS_EXTENDED } from "@/queries/GetAreasExtended";
import { UPSERT_AREA } from "@/queries/UpsertArea";
import { useMutation, useQuery } from "@apollo/client";
import { useRefresh, useNotify, RecordContext, SimpleForm, Toolbar, SaveButton, TextInput, AutocompleteArrayInput, ReferenceArrayInput, NumberInput, BooleanInput, } from "react-admin";
import { useTranslation } from "react-i18next";
import SelectArea from "@/components/area/SelectArea";
import { PageHeaderStyles } from "@/components/styles/PageHeaderStyles";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
import { EEarlyVotingPolicy } from "@sequentech/ui-core";
/**
 * FormContent component for creating or updating an Area entity.
 *
 * This component renders a form for area details, including name, description, contests, parent area, and hidden fields for election event and tenant.
 * It handles form submission using a GraphQL mutation, provides notifications on success or error, and refreshes the data.
 *
 * @param props - The properties for the form, including:
 *   - record: The current area record (if editing).
 *   - id: The area ID (if editing).
 *   - electionEventId: The election event ID associated with the area.
 *   - close: Optional callback to close the form/modal.
 *
 * @returns A React element rendering the area form.
 *
 * @remarks
 * - Uses react-admin components for form rendering and submission.
 * - Integrates with GraphQL for data fetching and mutations.
 * - Supports contest selection with autocomplete and filtering.
 */
export var FormContent = function (props) {
    var record = props.record, id = props.id, electionEventId = props.electionEventId, close = props.close, area_presentation = props.area_presentation, weightedVotingForAreas = props.weightedVotingForAreas;
    var refresh = useRefresh();
    var notify = useNotify();
    var t = useTranslation().t;
    var tenantId = useTenantStore()[0];
    var aliasRenderer = useAliasRenderer();
    var contestFilterToQuery = function (searchText) {
        if (!searchText || searchText.length == 0) {
            return { name: "" };
        }
        return { "name@_ilike,alias@_ilike": searchText.trim() };
    };
    var upsertArea = useMutation(UPSERT_AREA, {
        refetchQueries: [
            {
                query: GET_AREAS_EXTENDED,
                variables: {
                    electionEventId: electionEventId,
                    areaId: id,
                },
            },
        ],
    })[0];
    var getAllowEarlyVotingDefaultValue = function () {
        return area_presentation
            ? area_presentation.allow_early_voting === EEarlyVotingPolicy.ALLOW_EARLY_VOTING
            : false;
    };
    var isEarlyVotingChannelEnabled = function () {
        var _a;
        return ((_a = record === null || record === void 0 ? void 0 : record.voting_channels) === null || _a === void 0 ? void 0 : _a.early_voting) == true;
    };
    var areas = useQuery(GET_AREAS_EXTENDED, {
        variables: {
            electionEventId: electionEventId,
            areaId: id,
        },
    }).data;
    var parseValues = function (incoming) {
        var _a;
        var temp = __assign({}, incoming);
        temp.area_contest_ids = (_a = areas === null || areas === void 0 ? void 0 : areas.sequent_backend_area_contest) === null || _a === void 0 ? void 0 : _a.map(function (area) { return area.contest.id; });
        return temp;
    };
    var onSubmit = function (values) { return __awaiter(void 0, void 0, void 0, function () {
        var data, e_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    if (!values.name || ((_a = values.name) === null || _a === void 0 ? void 0 : _a.length) == 0) {
                        if (close) {
                            close();
                            notify(t("areas.createAreaError"), { type: "error" });
                            return [2 /*return*/];
                        }
                    }
                    return [4 /*yield*/, upsertArea({
                            variables: {
                                id: values.id,
                                name: values.name,
                                description: values.description,
                                tenantId: tenantId,
                                electionEventId: electionEventId,
                                parentId: values.parent_id,
                                areaContestsIds: values.area_contest_ids,
                                annotations: values.annotations,
                                labels: values.labels,
                                type: values.type,
                                allow_early_voting: values.allow_early_voting_boolean
                                    ? EarlyVotingPolicy.AllowEarlyVoting
                                    : EarlyVotingPolicy.NoEarlyVoting,
                            },
                        })];
                case 1:
                    data = (_b.sent()).data;
                    refresh();
                    if (values.id) {
                        notify(t("areas.updateAreaSuccess"), { type: "success" });
                    }
                    else {
                        notify(t("areas.createAreaSuccess"), { type: "success" });
                    }
                    if (close) {
                        close();
                    }
                    return [3 /*break*/, 3];
                case 2:
                    e_1 = _b.sent();
                    console.log("aa error creating", e_1);
                    refresh();
                    notify(t("areas.createAreaError"), { type: "error" });
                    if (close) {
                        close();
                    }
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    return (_jsx(RecordContext.Consumer, { children: function (incoming) {
            var parsedValue = parseValues(incoming);
            return (_jsxs(SimpleForm, { record: parsedValue, onSubmit: onSubmit, toolbar: _jsx(Toolbar, { children: _jsx(SaveButton, {}) }), children: [_jsx(PageHeaderStyles.Title, { children: t("areas.common.title") }), _jsx(PageHeaderStyles.SubTitle, { children: t("areas.common.subTitle") }), _jsx(TextInput, { source: "name" }), _jsx(TextInput, { source: "description" }), _jsx(ReferenceArrayInput, { label: String(t("areas.sequent_backend_area_contest")), reference: "sequent_backend_contest", source: "area_contest_ids", filter: {
                            tenant_id: tenantId,
                            election_event_id: electionEventId,
                        }, perPage: 100, enableGetChoices: function (_a) {
                            var q = _a.q;
                            return q && q.length >= 3;
                        }, children: _jsx(AutocompleteArrayInput, { className: "area-contest", fullWidth: true, optionText: aliasRenderer, filterToQuery: contestFilterToQuery, debounce: 100 }) }), _jsx(SelectArea, { tenantId: tenantId, electionEventId: electionEventId, source: "parent_id" }), record ? (_jsxs(_Fragment, { children: [_jsx(TextInput, { label: "Election Event", source: "election_event_id", defaultValue: (record === null || record === void 0 ? void 0 : record.id) || "", style: { display: "none" } }), _jsx(TextInput, { label: "Tenant", source: "tenant_id", defaultValue: (record === null || record === void 0 ? void 0 : record.tenant_id) || "", style: { display: "none" } })] })) : null, weightedVotingForAreas && (_jsx(NumberInput, { label: "Weight", source: "annotations.weight", format: function (value) { return Math.floor(value); }, min: 1, defaultValue: 1 })), _jsx(BooleanInput, { source: "allow_early_voting_boolean", defaultValue: getAllowEarlyVotingDefaultValue(), label: String(t("areas.formImputs.allowEarlyVoting")), disabled: !isEarlyVotingChannelEnabled() })] }));
        } }));
};
